import { Fragment } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../primitives/Icon';

export interface StepperStep {
  label: string;
  /** Optional secondary text rendered under the label. */
  meta?: string;
}

export interface StepperProgressProps {
  steps: StepperStep[];
  /** Index of the current step. Steps with index < currentStep are rendered as done. */
  currentStep: number;
  /** Render vertically (stacked rows) instead of horizontally. */
  vertical?: boolean;
  className?: string;
}

/**
 * Numbered step indicator with done / active / future visual states.
 * Mirrors the JSX prototype's horizontal/vertical layout. Done steps
 * render a check icon; active and future steps render the step number.
 */
export function StepperProgress({
  steps,
  currentStep,
  vertical = false,
  className,
}: StepperProgressProps) {
  return (
    <ol
      className={cn(
        'flex',
        vertical ? 'flex-col gap-3' : 'flex-row items-center gap-3',
        className,
      )}
      aria-label="Progress"
    >
      {steps.map((step, i) => {
        const done = i < currentStep;
        const active = i === currentStep;
        const isLast = i === steps.length - 1;
        return (
          <Fragment key={i}>
            <li
              className={cn(
                'flex items-start gap-3',
                vertical ? 'w-full' : 'flex-shrink-0',
              )}
              aria-current={active ? 'step' : undefined}
            >
              <span
                className={cn(
                  'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border text-caption font-semibold transition-colors',
                  done && 'border-accent-500 bg-accent-500 text-white',
                  active && 'border-accent-500 bg-bg-brand-soft text-text-brand',
                  !done && !active && 'border-border bg-bg-surface text-text-muted',
                )}
              >
                {done ? <Icon name="check" size={12} /> : <span>{i + 1}</span>}
              </span>
              <div className="flex flex-col">
                <span
                  className={cn(
                    'text-body',
                    done || active ? 'text-text-primary' : 'text-text-muted',
                  )}
                >
                  {step.label}
                </span>
                {step.meta ? (
                  <span className="text-caption text-text-muted">{step.meta}</span>
                ) : null}
              </div>
            </li>
            {!isLast ? (
              <span
                className={cn(
                  'flex-1 transition-colors',
                  vertical ? 'ml-3.5 w-px self-stretch' : 'h-px',
                  done ? 'bg-accent-500' : 'bg-border',
                )}
                aria-hidden
              />
            ) : null}
          </Fragment>
        );
      })}
    </ol>
  );
}
